import {
  approach,
  easeInOutCubic,
  easeInQuad,
  easeOutCubic,
  easeOutExpo,
  lerp,
  pneu,
  retract,
  seg,
  wobble,
  zoom,
} from './easing'

/**
 * The pad-print film as a pure function of its progress p (0..1) and of the
 * loop it belongs to. Returns the custom properties the stage reads (`--pp-*`,
 * without prefix) and the live station.
 *
 * It loops without a cut: the machine ends where it starts (carriage home,
 * cup sealed) and the camera returns to the establishing shot. A later loop
 * opens on the cap the previous one printed, its register clears, and the
 * printed cap is exchanged for a blank one while the camera is on the cliché
 * and the fixture is out of frame.
 */

const PAD_GEOMETRY = { cliche: 484, part: 1124, drop: 104, over: 48 }
const CUP = { sealed: 484, park: 343 }

const WIDE = { s: 1, x: 800, y: 455 }
const AT_CLICHE = { s: 1.6, x: 470, y: 472 }
const AT_PART = { s: 2, x: 1160, y: 500 }
/* The breathe makes room: the cap settles on the left third, the register
   label takes the right. */
const AT_PART_CLOSE = { s: 2.05, x: 1215, y: 492 }

/* [start, end, from, to, linear?] — the camera never moves during a squash or dwell. */
const CAMERA = [
  [0.06, 0.18, WIDE, AT_CLICHE],
  [0.3, 0.4, AT_CLICHE, WIDE],
  [0.44, 0.54, WIDE, AT_PART],
  [0.72, 0.86, AT_PART, AT_PART_CLOSE],
  [0.86, 0.97, AT_PART_CLOSE, WIDE],
]

/* Contact windows: approach start, contact, squashed, dwell end, lifted. */
const PICK = [0.14, 0.195, 0.245, 0.265, 0.345]
const PRINT = [0.52, 0.57, 0.62, 0.64, 0.74]

/* The camera holds the cliché from 0.18 to 0.3, the fixture out of frame. */
const PART_SWAP = 0.2

export const PAD_STATIONS = [0, 0.14, 0.49]
export const PAD_BEATS = [0.195, 0.57, 0.72]

/** The camera along its keyed moves (focus eased, scale geometric). */
export function cameraAt(p, keys) {
  for (const [a, b, from, to, linear] of keys) {
    if (p < a) return from
    if (p <= b) {
      const k = seg(p, a, b)
      const e = linear ? k : easeInOutCubic(k)
      return { s: zoom(from.s, to.s, e), x: lerp(from.x, to.x, e), y: lerp(from.y, to.y, e) }
    }
  }
  return keys[keys.length - 1][3]
}

/** Camera values as stage variables. */
export const cameraVars = (cam) => ({ 'cam-s': cam.s, 'cam-x': cam.x, 'cam-y': cam.y })

/**
 * Vertical travel (dy) and overtravel (o) through one contact window. After
 * the dwell, peel and lift are one retraction from drop + over to rest: the
 * overtravel is what is left of it below the contact plane.
 */
function press(p, [a, c, s, d, l]) {
  const { drop, over } = PAD_GEOMETRY
  if (p <= a || p >= l) return { dy: 0, o: 0, rolling: 0 }
  if (p < c) return { dy: drop * approach(seg(p, a, c)), o: 0, rolling: 0 }
  if (p < s) {
    const o = over * easeOutCubic(seg(p, c, s))
    return { dy: drop + o, o, rolling: 1 }
  }
  if (p < d) return { dy: drop + over, o: over, rolling: 0 }
  const dy = (drop + over) * (1 - retract(seg(p, d, l), 2 / (drop + over)))
  return { dy, o: Math.max(0, dy - drop), rolling: 0 }
}

/* The shuttle stops 1.6 units past its mark and cushions back. */
const SHUTTLE_OVER = 0.0025

function padX(p) {
  const { cliche, part } = PAD_GEOMETRY
  if (p < 0.345) return cliche
  if (p < 0.48) return lerp(cliche, part, pneu(seg(p, 0.345, 0.48), SHUTTLE_OVER))
  if (p < 0.86) return part
  return lerp(part, cliche, pneu(seg(p, 0.86, 0.95), SHUTTLE_OVER))
}

function cupX(p) {
  if (p < 0.34) return lerp(CUP.sealed, CUP.park, pneu(seg(p, 0.04, 0.14)))
  return lerp(CUP.park, CUP.sealed, pneu(seg(p, 0.34, 0.42)))
}

/** Detail A: 0..0.2 the source ring and leader, 0.2..1 the bubble opening. */
function detail(p) {
  if (p < 0.185) return 0
  if (p < 0.19) return 0.2 * easeOutCubic(seg(p, 0.185, 0.19))
  if (p < 0.47) return 0.2 + 0.8 * easeOutCubic(seg(p, 0.19, 0.21))
  return 1 - easeInQuad(seg(p, 0.47, 0.5))
}

/**
 * Ink states: every swap happens under full cover (cup or squashed pad), and
 * the part exchange happens off camera. `carried` is true when the loop opens
 * on the previous loop's printed cap.
 */
function inks(p, carried) {
  const picked = p >= 0.255 && p < 0.42
  return {
    'etch-ink': picked ? 0 : 1,
    'etch-res': picked ? 1 : 0,
    lip: p >= 0.255 && p < 0.63 ? 1 : 0,
    'part-ink': p >= 0.63 || (carried && p < PART_SWAP) ? 1 : 0,
  }
}

/** The register payoff (crops, leader, label) on the pad timing; other films pass a shifted p. */
export function register(p) {
  const crops = Object.fromEntries(
    [0, 1, 2, 3].map((i) => [`crop-${i}`, easeOutExpo(seg(p, 0.72 + 0.012 * i, 0.76 + 0.012 * i))]),
  )
  return {
    ...crops,
    // Any positive value shows the leader dot; the line draws over 0.77..0.79.
    leader: p < 0.765 ? 0 : Math.max(0.001, easeOutCubic(seg(p, 0.77, 0.79))),
    label: seg(p, 0.79, 0.81),
  }
}

/* A loop that opens on a printed cap first clears its register: the payoff
   runs backwards (label, leader, crops) as the camera leaves. */
const unregister = (p) => register(0.81 - 1.5 * p)

/**
 * @param {number} p film progress 0..1
 * @param {number} [cycle] loops completed before this one
 */
export function padFrame(p, cycle = 0) {
  const carried = cycle > 0
  const atPart = p >= 0.4
  const { dy, o, rolling } = press(p, atPart ? PRINT : PICK)
  const squash = o / PAD_GEOMETRY.over
  const skew = wobble(seg(p, 0.48, 0.52)) - wobble(seg(p, 0.95, 0.99))
  return {
    vars: {
      ...cameraVars(cameraAt(p, CAMERA)),
      'pad-x': padX(p),
      'pad-dy': dy,
      'pad-o': o,
      'pad-skew': skew,
      foot: Math.sqrt(squash),
      front: rolling,
      'cup-x': cupX(p),
      ...inks(p, carried),
      detail: detail(p),
      bloom: p < 0.195 ? 0 : p < 0.245 ? Math.sqrt(squash) : 1,
      ...(atPart || !carried ? register(p) : unregister(p)),
    },
    station: p < PAD_STATIONS[1] ? 0 : p < PAD_STATIONS[2] ? 1 : 2,
  }
}

/** The still: the machine home, the cap printed and in register, the whole set in shot. */
export const padPoster = () => padFrame(0.999, 1)
